import {
  Component,
  ContentChild,
  Input,
  Output,
  OnInit,
  EventEmitter,
  ChangeDetectionStrategy,
  OnDestroy,
} from '@angular/core'

import {Observable, Subject, of} from 'rxjs'
import {takeUntil, take} from 'rxjs/operators'

import {FilterListHeaderDirective} from './filter-list-header.directive'
import {FilterListItemHeaderDirective} from './filter-list-item-header.directive'
import {FilterListItemExpandDirective} from './filter-list-item-expand.directive'
import {ScrollListDirective} from '../scroll-list/scroll-list.directive'
import {
  SearchFilterComponent,
  FilterOptions,
  FilterQuery,
} from '@shared/search-filter/search-filter.component'
import {
  DateFilter,
  DateFilterEnum,
} from '@app/core/services/date-filter/date-filter.interface'
import {NotificationService} from '@app/core/services/notification.service'
import {FilterListExtraBarDirective} from './filter-list-extra-bar.directive'
import {ContextMenuService} from '@app/core/services/context-menu.service'
import {SelectionService} from '@app/features/terminal-park/selection-service'
import {FilterListItemHeaderLabelDirective} from './filter-list-item-header-label.directive'

export interface Action {
  name: string
  icon?: string
  action(): Observable<void>
  disabled?(): boolean
  permissions?: string[]
}

export type ItemSelected<T> = (item: T) => Observable<boolean>

export interface ItemAction<T> {
  name: string
  icon?: string
  action(item: T): Observable<void>
}

@Component({
  selector: 'filter-list',
  templateUrl: 'filter-list.component.html',
  styleUrls: ['filter-list.component.scss'],
  providers: [ScrollListDirective, SearchFilterComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FilterListComponent<T> implements OnInit, OnDestroy {
  @ContentChild(FilterListHeaderDirective, {static: true})
  listHeader: FilterListHeaderDirective
  @ContentChild(FilterListItemHeaderDirective, {static: true})
  itemHeader: FilterListItemHeaderDirective
  @ContentChild(FilterListItemExpandDirective, {static: true})
  itemExpand: FilterListItemExpandDirective
  @ContentChild(FilterListExtraBarDirective, {static: true})
  extraBar: FilterListExtraBarDirective
  @ContentChild(FilterListItemHeaderLabelDirective, {static: true})
  itemHeaderLabel: FilterListItemHeaderLabelDirective

  @Input() items: Observable<T[]> = of([])
  @Input() loading: Observable<boolean> = of(false)
  @Input() total: Observable<number>
  @Input() transactionFilter = false

  @Input() actions: Action[]
  @Input() itemActions: ItemAction<T>[]
  @Input() selected: ItemSelected<T>

  @Input() loadData

  @Input() filter: FilterOptions
  @Input() dateType: DateFilterEnum

  @Input() selectable = false
  @Input() pageSize: 20

  @Input() noItemsMessage?: string
  @Input() itemClasses: (item: T) => string = () => ''
  @Input() allItems: Observable<T[]>

  @Output() onFilterChanged: EventEmitter<FilterQuery> =
    new EventEmitter<FilterQuery>()
  @Output() onCheckedChanged: EventEmitter<T[]> = new EventEmitter<T[]>()
  @Output() onDateChanged: EventEmitter<DateFilter> =
    new EventEmitter<DateFilter>()
  @Output() onClick: EventEmitter<T> = new EventEmitter<T>()
  @Output() onRightClick: EventEmitter<T> = new EventEmitter<T>()

  @Output() getAllList: EventEmitter<any> = new EventEmitter<any>()

  @Input() checked: any[] = []
  @Input() checkedIndex: string
  @Input() headerLabels: boolean = false
  listTotal = 0
  selectAll = false
  selectAllNext = false
  show: Map<T, boolean> = new Map<T, boolean>()
  firstRow = null
  lastRow = null
  listVisible = 0
  @Input() enableCustomContextMenu = true

  constructor(
    private notification: NotificationService,
    public customContextMenuService: ContextMenuService,
    private selection: SelectionService,
  ) {}

  ngOnInit(): void {
    if (this.selectable) {
      this.checked = this.selection.getSelected()
    }
    if (this.enableCustomContextMenu) {
      this.customContextMenuService
        .getContextMenuEnabled()
        .subscribe((enabled) => {
          this.enableCustomContextMenu = enabled
        })
    }

    this.onCheckedChanged.subscribe((change) => {
      this.selection.setSelected(change)
      this.checked = this.selection.getSelected()
    })

    this.items.pipe(takeUntil(this.end$)).subscribe((items) => {
      if (this.selectAllNext) {
        this.checkAll()
        this.selectAllNext = false
      }
      this.listVisible = items.length
      const ordered: T[] = items.filter((item) => {
        const check = this.checked.map((item) => item[this.checkedIndex])
        return check.includes(item[this.checkedIndex])
      })
      this.onCheckedChanged.emit(ordered)
    })

    this.total.pipe(takeUntil(this.end$)).subscribe((t) => {
      this.listTotal = t
    })
  }

  private end$: Subject<void> = new Subject<void>()
  ngOnDestroy() {
    this.end$.next()
    this.end$.complete()
  }

  itemChecked(item: T, i: number, event: any): void {
    const checkedItems = this.checked
    if (event.target.checked) {
      const isAlreadyChecked = checkedItems.some(
        (checkedItem) =>
          checkedItem[this.checkedIndex] === item[this.checkedIndex],
      )
      if (!isAlreadyChecked) {
        this.firstRow = i
        checkedItems.push(item)
      }
    } else {
      const checked = checkedItems.map(
        (checkedItem) => checkedItem[this.checkedIndex],
      )
      const index = checked.indexOf(item[this.checkedIndex])
      if (index !== -1) {
        checkedItems.splice(index, 1)
      }
    }
    this.onCheckedChanged.emit(checkedItems)
  }

  fillCheckedItems(checkedItem: any) {
    const checked = this.checked.map((item) => item[this.checkedIndex])
    return checked.includes(checkedItem[this.checkedIndex])
  }

  onItemSelected(item: T, event: any): void {
    event.stopPropagation()
    if (this.selected) {
      // if we have a registered select handler call it
      this.selected(item)
        .pipe(takeUntil(this.end$))
        .subscribe((toggle: boolean) => {
          if (toggle) {
            // and toggle where needed
            this.onToggleShow(item)
          }
        })
    } else {
      // otherwise just toggle the expansion
      this.onToggleShow(item)
    }
  }

  expandable(item: T): boolean {
    if (this.itemExpand) {
      return this.itemExpand.expandable(item)
    }
    return false
  }

  onScroll() {
    if (this.loadData) {
      this.loadData(this.pageSize)
    }
  }

  onRowClick(item: T, event: any): void {
    event.stopPropagation()
    this.onClick.emit(item)
  }

  onRowRightClick(item: T, event: any): void {
    if (this.enableCustomContextMenu) {
      // only when we enable custom context menu do we prevent default from opening
      event.preventDefault()
      event.stopPropagation()
      this.onRightClick.emit({
        ...item,
        clientX: event.clientX - 200,
        clientY: event.clientY - 110,
      }) // should rename this to custom context emitter
    }
  }

  // prevent unwanted item selected handling on certain click events
  setNotSelectItem(event: any): void {
    this.firstRow = null
    this.lastRow = null
    event.stopPropagation()
    // we cannot use stopPropagation because the ngxDropdown depends on the event bubbling
  }

  onToggleShow(item: T): void {
    if (this.expandable(item)) {
      if (!this.show.has(item)) {
        this.show.set(item, false)
      }
      this.show.set(item, !this.show.get(item))
    }
  }

  onFilterChange = (query: FilterQuery): void => {
    this.onFilterChanged.emit(query)
  }

  onDateChange = (range: DateFilter): void => {
    this.onDateChanged.emit(range)
  }

  checkAll() {
    this.items.pipe(take(1)).subscribe((items) => {
      this.checked = items.slice()

      this.onCheckedChanged.emit(this.checked)
    })
  }

  clearChecked() {
    this.checked = []
    this.onCheckedChanged.emit([])
  }

  isClear() {
    return this.checked.length !== this.listVisible
  }

  getCheckState(): string {
    return this.isClear() ? 'fa-square' : 'fa-check-square'
  }

  toggleChecked() {
    this.selectAll = !this.isClear()
    if (this.selectAll === false) {
      if (this.listTotal > 1000) {
        this.notification.warn(
          'Maximum of 1000 items can be selected. Please use filters to reduce the results.',
        )
      } else {
        // reload all data and check them all
        this.selectAllNext = true
        this.loadData(this.listTotal)
        this.selectAll = true
      }
    } else {
      this.clearChecked()
      this.selectAll = false
    }
  }

  multipleToggle(i, event) {
    event.stopPropagation()

    if (this.firstRow !== null && this.lastRow !== null) {
      this.firstRow = this.lastRow
      this.lastRow = null
    }

    this.firstRow === null ? (this.firstRow = i) : (this.lastRow = i)

    if (this.lastRow !== null && this.firstRow > this.lastRow) {
      const first = this.firstRow
      this.firstRow = this.lastRow
      this.lastRow = first
    }

    this.items.pipe(take(1)).subscribe((items) => {
      items.forEach((item, index) => {
        if (index >= this.firstRow && index <= this.lastRow) {
          this.itemChecked(item, index, event)
        }
      })
    })
  }

  prepareCSVData() {
    this.getAllList.emit()
  }
}
